import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getExhibitManifest, offeredExhibitKeys } from "@alm/domain";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { AdmissionFieldRequirementList } from "@/routes/_authenticated/_administration/settings/-components/forms/admission-field-requirement-list";
import { FormPreview } from "@/routes/_authenticated/_administration/settings/-components/forms/form-preview";
import { useAdmissionFacility } from "@/routes/_authenticated/_administration/settings/-components/forms/use-admission-facility";

/* Built on first use, not at module load: a top-level call here keeps the whole
   exhibit registry in the route tree that every page of the app downloads. */
let residentForms: { key: string; name: string }[] | undefined;
function formsOffered() {
  residentForms ??= offeredExhibitKeys("resident")
    .map((key) => ({ key, name: getExhibitManifest(key)?.name ?? key }))
    .toSorted((a, b) => a.name.localeCompare(b.name));
  return residentForms;
}

const VIEWS = ["preview", "fields"] as const;
type View = (typeof VIEWS)[number];
const isView = (value: unknown): value is View => VIEWS.some((view) => view === value);

export const Route = createFileRoute("/_authenticated/_administration/settings/move-in/fields")({
  validateSearch: (search: Record<string, unknown>): { form?: string; view?: View } => ({
    // a plain string here, checked against the offered forms in the page
    form: typeof search.form === "string" ? search.form : undefined,
    view: isView(search.view) ? search.view : undefined,
  }),
  component: AdmissionFormSettings,
});

/** Each admission form as its signers read it, and the fields a building insists on before it
 * counts as finished (guided intake asks only those). Form and view live in the URL; the building
 * is the one in view. */
function AdmissionFormSettings() {
  const search = Route.useSearch();
  const facility = useAdmissionFacility();
  // Building admins hold this at their building, never company-wide, so ask about this one.
  const canManage = useCan("settings:manage", facility.facilityId);
  const navigate = Route.useNavigate();
  const forms = formsOffered();
  const exhibitKey = forms.find((form) => form.key === search.form)?.key ?? forms[0]?.key ?? "";
  const view = search.view ?? "preview";
  const setExhibitKey = (form: string) =>
    void navigate({ search: (prev) => ({ ...prev, form }), replace: true });
  const [formSearch, setFormSearch] = useState("");
  const manifest = getExhibitManifest(exhibitKey);

  const settings = useQuery({
    ...orpc.care.admissions.exhibitSettings.get.queryOptions({
      input: { facilityId: facility.facilityId },
    }),
    enabled: facility.facilityId !== "",
  });

  const overrides: Record<string, boolean> = {};
  for (const row of settings.data?.requirements ?? []) {
    if (row.exhibitKey === exhibitKey) overrides[row.fieldKey] = row.required;
  }

  return (
    <>
      <div className="flex flex-wrap items-start gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="admission-form-search">Find a form</Label>
          <Input
            id="admission-form-search"
            className="w-full max-w-xs"
            placeholder="Type part of its name"
            autoComplete="off"
            value={formSearch}
            onChange={(event) => setFormSearch(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="admission-form">Form</Label>
          <Select value={exhibitKey} onValueChange={setExhibitKey}>
            <SelectTrigger id="admission-form" className="w-full max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* The chosen form stays in the list whatever is typed, or the
                  trigger would have nothing to name. */}
              {forms
                .filter(
                  (form) =>
                    form.key === exhibitKey ||
                    form.name.toLowerCase().includes(formSearch.trim().toLowerCase()),
                )
                .map((form) => (
                  <SelectItem key={form.key} value={form.key}>
                    {form.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {settings.isError ? (
        <ReadFailed
          message="This building’s own requirements could not be loaded, so what is shown below is the standard set rather than yours."
          error={settings.error}
          retrying={settings.isFetching}
          onRetry={() => void settings.refetch()}
        />
      ) : null}

      {manifest ? (
        <Tabs
          value={view}
          onValueChange={(next) => {
            if (isView(next))
              void navigate({ search: (prev) => ({ ...prev, view: next }), replace: true });
          }}
        >
          <TabsList>
            <TabsTrigger value="preview">Preview</TabsTrigger>
            <TabsTrigger value="fields">Required fields</TabsTrigger>
          </TabsList>
          <TabsContent value="preview">
            {facility.facilityId === "" || settings.isPending ? (
              <Skeleton className="h-96 w-full" />
            ) : (
              <FormPreview key={manifest.key} manifest={manifest} overrides={overrides} />
            )}
          </TabsContent>
          <TabsContent value="fields">
            <RecordCard title={manifest.name} canWrite={canManage}>
              <AdmissionFieldRequirementList
                facilityId={facility.facilityId}
                manifest={manifest}
                overrides={overrides}
                canManage={canManage}
                isLoading={facility.facilityId === "" || settings.isPending}
              />
            </RecordCard>
          </TabsContent>
        </Tabs>
      ) : null}
    </>
  );
}
